// BL-5103: one result shape for access code redemption, whichever backend
// served it — the redeemSectionPassCoupon cloud function (legacy) or the Adonis
// endpoint (when featureFlags.redeemAccessCodesOnServer is on).

export type RedeemAccessCodeErrorCode =
  | 'INVALID_CODE'
  | 'ALREADY_REDEEMED'
  | 'EXPIRED'
  | 'PRICE_MISMATCH'
  | 'ALREADY_HAS_PASS'
  | 'SECTION_NOT_ELIGIBLE'
  | 'RATE_LIMITED'
  // network failure, timeout waiting for access, or anything unexpected
  | 'UNKNOWN'

/** Mirrors RedeemAccessCodeErrorDetails in apps/server/app/services/access_codes/redeem.ts */
export type RedeemAccessCodeErrorDetails = {
  // EXPIRED, ISO 8601
  expiresAt?: string
  // ALREADY_REDEEMED by the student asking, and the class they used it in
  redeemedByYou?: boolean
  redeemedIn?: string
  // PRICE_MISMATCH, cents
  codePrice?: number
  sectionPrice?: number
}

export type RedeemAccessCodeResult =
  | { success: true }
  | {
      success: false
      errorCode: RedeemAccessCodeErrorCode
      // only the Adonis endpoint sends these
      details?: RedeemAccessCodeErrorDetails
    }

const SERVER_ERROR_CODES = new Set<RedeemAccessCodeErrorCode>([
  'INVALID_CODE',
  'ALREADY_REDEEMED',
  'EXPIRED',
  'PRICE_MISMATCH',
  'ALREADY_HAS_PASS',
  'SECTION_NOT_ELIGIBLE',
])

export const redeemFailure = (
  errorCode: RedeemAccessCodeErrorCode,
  details?: RedeemAccessCodeErrorDetails
): RedeemAccessCodeResult =>
  details
    ? { success: false, errorCode, details }
    : { success: false, errorCode }

/**
 * The cloud function answers true, false/null (not found or ineligible), or
 * one of two hard-coded strings.
 */
export function fromCloudFunctionResult(
  data: boolean | null | undefined | string
): RedeemAccessCodeResult {
  if (data === true) return { success: true }
  if (data === 'coupon already consumed')
    return redeemFailure('ALREADY_REDEEMED')
  if (data === 'coupon expired') return redeemFailure('EXPIRED')
  return redeemFailure('INVALID_CODE')
}

/**
 * The Adonis endpoint answers 4xx with `{ success: false, errorCode }`, or 429
 * from the rate limiter. The api client throws on non-2xx with `status` and
 * the parsed body on `response`.
 */
export function fromServerError(error: unknown): RedeemAccessCodeResult {
  const { status, response } = (error ?? {}) as {
    status?: number
    response?: { errorCode?: unknown; details?: unknown }
  }
  if (status === 429) return redeemFailure('RATE_LIMITED')
  const errorCode = response?.errorCode
  if (
    typeof errorCode === 'string' &&
    SERVER_ERROR_CODES.has(errorCode as RedeemAccessCodeErrorCode)
  ) {
    return redeemFailure(
      errorCode as RedeemAccessCodeErrorCode,
      parseDetails(response?.details)
    )
  }
  return redeemFailure('UNKNOWN')
}

/** Keeps only well-typed fields; the dialog falls back to generic copy. */
function parseDetails(raw: unknown): RedeemAccessCodeErrorDetails | undefined {
  if (!raw || typeof raw !== 'object') return undefined
  const { expiresAt, codePrice, sectionPrice, redeemedByYou, redeemedIn } =
    raw as Record<string, unknown>
  const details: RedeemAccessCodeErrorDetails = {}
  if (typeof expiresAt === 'string') details.expiresAt = expiresAt
  if (typeof codePrice === 'number') details.codePrice = codePrice
  if (typeof sectionPrice === 'number') details.sectionPrice = sectionPrice
  if (redeemedByYou === true) details.redeemedByYou = true
  if (typeof redeemedIn === 'string') details.redeemedIn = redeemedIn
  return Object.keys(details).length ? details : undefined
}
